import { YStack, XStack, Text } from "tamagui";
import { format } from "date-fns";
import { colors } from "@styles/colors";
import { SkinCard, v2 } from "@screen/Home/pages/GasShop/skin";
import { isOrderAmountHidden } from "@utils/func/orderAmountVisibility";
import { OrderStatusBadge, toOrderUIStatus } from "./OrderStatusBadge";

/**
 * 訂單卡（Skin V2）。訂單列表與帳戶的訂單紀錄共用同一張。
 *
 * 為什麼要共用：兩邊原本各刻一張——列表是「地址／品項／單號＋金額」三行、帳戶那份是
 * 「單號＋狀態／品項＋金額／送至＋付款」外加上下分隔線。同一筆訂單在兩個頁面長得不一樣，
 * 客戶會以為看到的是不同東西。資料欄位其實一致（display_id / owner.label / items_summary /
 * total / hide_amount_on_pending / status），沒有理由分兩份。
 *
 * 版面只有一個視覺錨點：藍色只留給總價，狀態小標的顏色是語意不是裝飾。
 */
export interface OrderCardData {
  display_id: string;
  owner?: { label?: string } | null;
  items_summary: { pn: string; qty: number }[];
  total: number;
  created_at?: string | null;
  /** 後端狀態字串（pending / delivering / …） */
  status?: string;
  /** 後端已給的狀態文案；沒有就用共用色表的文案 */
  status_label?: string;
  hide_amount_on_pending?: boolean;
  refund_state?: "none" | "running" | "done";
}

export const OrderCardV2 = ({ o, onPress }: { o: OrderCardData; onPress: () => void }) => {
  const uiStatus = toOrderUIStatus(o.status);
  // 該瓦斯行開啟隱藏金額設定、且訂單為待確認 → 不顯示金額（待瓦斯行確認後才顯示）
  const hideAmount = isOrderAmountHidden(o.hide_amount_on_pending, uiStatus);
  const summary = o.items_summary.map((it) => `${it.pn} ×${it.qty}`).join("、");
  const time = o.created_at ? format(new Date(o.created_at), "MM/dd HH:mm") : "-";

  return (
    // SkinCard 不吃 onPress，外面包一層負責點擊與按壓回饋
    <YStack onPress={onPress} cursor="pointer" pressStyle={{ scale: 0.985 }} animation="quick">
      <SkinCard>
        <YStack gap={8}>
          <XStack jc="space-between" ai="center" gap="$3">
            <Text fontSize={15} fow="700" color={v2.title} numberOfLines={1} flex={1}>
              {o.owner?.label || "配送地址"}
            </Text>
            <OrderStatusBadge status={o.status} label={o.status_label} refundState={o.refund_state} />
          </XStack>
          <Text fontSize={13} color={v2.body} numberOfLines={1} ellipsizeMode="tail">
            {summary}
          </Text>
          <XStack jc="space-between" ai="flex-end" gap="$3">
            <Text fontSize={12} color={v2.sub} numberOfLines={1} flex={1}>
              {`#${o.display_id} · ${time}`}
            </Text>
            {!hideAmount && (
              <Text fontSize={18} fow="800" color={colors.primary} ls={-0.5} flexShrink={0}>
                NT$ {o.total.toLocaleString()}
              </Text>
            )}
          </XStack>
        </YStack>
      </SkinCard>
    </YStack>
  );
};
